import { Container, Graphics } from 'pixi.js';

/**
 * The lab's seven-segment countdown — the pressure the case opens on
 * ("the node will be disconnected in ten minutes").
 *
 * Drawn vectorially rather than scaled from a bitmap so it stays crisp on any
 * screen, matching the seven-segment display built for the Case 000 pilot
 * (tools/blender/create_case000_pilot_v2.py).
 *
 * It is a TENSION device, not a fail state: the shared game engine has no
 * concept of elapsed time, so reaching 00:00 must never decide the outcome.
 * It switches to an alarm look and keeps the case playable.
 */
const SEGMENTS: Record<string, readonly [number, number, number, number]> = {
  // name: [centerX, centerY, width, height] in a 1x2 digit box
  a: [0.0, 0.86, 0.62, 0.13],
  b: [0.33, 0.45, 0.13, 0.7],
  c: [0.33, -0.45, 0.13, 0.7],
  d: [0.0, -0.86, 0.62, 0.13],
  e: [-0.33, -0.45, 0.13, 0.7],
  f: [-0.33, 0.45, 0.13, 0.7],
  g: [0.0, 0.0, 0.62, 0.13],
};

const DIGIT_SEGMENTS: Record<string, string> = {
  '0': 'abcdef',
  '1': 'bc',
  '2': 'abdeg',
  '3': 'abcdg',
  '4': 'bcfg',
  '5': 'acdfg',
  '6': 'acdefg',
  '7': 'abc',
  '8': 'abcdefg',
  '9': 'abcdfg',
};

export class Countdown {
  readonly view = new Container();
  private readonly digits = new Graphics();
  private readonly panel = new Graphics();
  private remainingMs: number;
  private readonly scale: number;
  private lastRendered = '';
  private blinkMs = 0;
  private running = false;

  /** @param totalSeconds starting value, e.g. 600 for ten minutes. */
  constructor(totalSeconds: number, scale = 1) {
    this.remainingMs = totalSeconds * 1000;
    this.scale = scale;
    this.view.addChild(this.panel, this.digits);
    this.drawPanel();
    this.render(true);
  }

  get expired(): boolean {
    return this.remainingMs <= 0;
  }

  /** Restarts the countdown at `totalSeconds`, for a fresh playthrough on replay. */
  reset(totalSeconds: number): void {
    this.remainingMs = totalSeconds * 1000;
    this.blinkMs = 0;
    this.running = false;
    this.render(true);
  }

  /** Starts the clock once the briefing is over and the player can act. */
  start(): void {
    this.running = true;
  }

  pause(): void {
    this.running = false;
  }

  private drawPanel(): void {
    const w = 92 * this.scale;
    const h = 34 * this.scale;
    this.panel
      .clear()
      .roundRect(-6 * this.scale, -h / 2 - 5 * this.scale, w, h, 5 * this.scale)
      .fill({ color: 0x1a0505, alpha: 0.88 })
      .stroke({ color: 0xff2d2d, width: 1.5, alpha: 0.75 });
  }

  update(deltaMs: number): void {
    if (this.running && this.remainingMs > 0) {
      this.remainingMs = Math.max(0, this.remainingMs - deltaMs);
    }
    this.blinkMs += deltaMs;
    this.render(false);
  }

  private text(): string {
    const total = Math.ceil(this.remainingMs / 1000);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  private render(force: boolean): void {
    const value = this.text();
    // When expired the display blinks, so it must redraw even if unchanged.
    const blinkOn = this.expired ? Math.floor(this.blinkMs / 450) % 2 === 0 : true;
    const key = `${value}|${String(blinkOn)}`;
    if (!force && key === this.lastRendered) return;
    this.lastRendered = key;

    const unit = 11 * this.scale; // digit box half-height
    const digitWidth = 13 * this.scale;
    const gap = 4 * this.scale;
    const color = this.expired ? 0xff3b30 : 0xff5a4a;
    const dim = 0x3a0f0f;

    this.digits.clear();
    let x = 0;
    for (const char of value) {
      if (char === ':') {
        const on = blinkOn;
        for (const dy of [0.42, -0.42]) {
          this.digits
            .circle(x + 3 * this.scale, dy * unit, 1.8 * this.scale)
            .fill({ color: on ? color : dim });
        }
        x += 8 * this.scale;
        continue;
      }
      const lit = DIGIT_SEGMENTS[char] ?? '';
      for (const [name, rect] of Object.entries(SEGMENTS)) {
        const [cx, cy, w, h] = rect;
        const isOn = lit.includes(name) && blinkOn;
        this.digits
          .roundRect(
            x + (cx - w / 2) * unit,
            -(cy + h / 2) * unit,
            w * unit,
            h * unit,
            1.2 * this.scale,
          )
          .fill({ color: isOn ? color : dim, alpha: isOn ? 1 : 0.5 });
      }
      x += digitWidth + gap;
    }
  }
}
